<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" @click.self="$emit('close')">
    <div data-tour="preferences-modal" class="w-full max-w-lg bg-surface dark:bg-surface-dark rounded-xl shadow-xl max-h-[90vh] flex flex-col">
      <div class="flex items-center justify-between px-6 py-4 border-b border-stroke-subtle dark:border-stroke-dark flex-shrink-0">
        <h2 class="text-base font-semibold text-fg-base dark:text-fg-base-dark">Preferences</h2>
        <button @click="$emit('close')" data-tour="preferences-close-btn" class="text-fg-faint hover:text-fg-secondary dark:hover:text-fg-secondary-dark">
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="px-6 py-2 overflow-y-auto divide-y divide-stroke-subtle dark:divide-stroke-dark">
        <p v-if="loading" class="text-sm text-fg-muted dark:text-fg-muted-dark py-4">Loading…</p>
        <p v-else-if="loadError" class="text-sm text-red-600 dark:text-red-400 py-4">{{ loadError }}</p>

        <template v-else>
          <!-- Preferred contact method -->
          <div class="flex items-center justify-between gap-3 py-4">
            <label for="pref-contact-method" class="flex items-center gap-3 text-sm text-fg-secondary dark:text-fg-secondary-dark">
              <ChatBubbleLeftRightIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
              Preferred contact method
            </label>
            <select
              id="pref-contact-method"
              v-model="preferredContactMethod"
              class="w-auto text-sm rounded-lg border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-dark pl-2.5 pr-7 py-1.5 text-fg-base dark:text-fg-base-dark flex-shrink-0"
            >
              <option value="either">Either</option>
              <option value="text">Text</option>
              <option value="email">Email</option>
            </select>
          </div>

          <!-- Staff notification channels (email / text per category).
               AppHeader opens this modal scrolled here for ?prefs=notifications —
               the target of the "Manage notification settings" link in staff emails. -->
          <div ref="notificationsSection" class="py-4">
            <button
              type="button"
              @click="notificationsOpen = !notificationsOpen"
              :aria-expanded="notificationsOpen"
              class="w-full flex items-center justify-between text-sm text-fg-secondary dark:text-fg-secondary-dark hover:text-fg-base dark:hover:text-fg-base-dark transition-colors"
            >
              <span class="flex items-center gap-3">
                <BellIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
                Notifications (email &amp; text)
              </span>
              <ChevronRightIcon :class="['h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0 transition-transform', notificationsOpen && 'rotate-90']" />
            </button>
            <div v-if="notificationsOpen" class="mt-3">
              <p class="text-xs text-fg-faint dark:text-fg-faint-dark mb-3">Changes save instantly.</p>
              <NotificationPrefsPanel />
            </div>
          </div>

          <!-- Change password -->
          <div class="py-4">
            <button
              type="button"
              @click="$emit('change-password')"
              class="w-full flex items-center justify-between text-sm text-fg-secondary dark:text-fg-secondary-dark hover:text-fg-base dark:hover:text-fg-base-dark transition-colors"
            >
              <span class="flex items-center gap-3">
                <KeyIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
                Change Password
              </span>
              <ChevronRightIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            </button>
          </div>

          <!-- Hide excused lessons -->
          <div class="flex items-start gap-3 py-4">
            <EyeSlashIcon class="h-4 w-4 mt-0.5 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            <div class="flex-1 min-w-0 flex items-start justify-between gap-3">
              <label for="pref-hide-excused" class="text-sm text-fg-secondary dark:text-fg-secondary-dark select-none">
                Hide excused lessons in calendar view
                <span class="block text-xs text-fg-faint dark:text-fg-faint-dark mt-0.5">
                  Still visible in Attendance; also excluded from your Google Calendar feed.
                </span>
              </label>
              <ToggleSwitch v-model="hideExcusedLessons" id="pref-hide-excused" class="mt-0.5" />
            </div>
          </div>

          <!-- Keyboard shortcuts -->
          <div class="flex items-start gap-3 py-4">
            <CommandLineIcon class="h-4 w-4 mt-0.5 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            <div class="flex-1 min-w-0 flex items-start justify-between gap-3">
              <label for="pref-shortcuts-enabled" class="text-sm text-fg-secondary dark:text-fg-secondary-dark select-none">
                Keyboard shortcuts
                <span class="block text-xs text-fg-faint dark:text-fg-faint-dark mt-0.5">
                  Press <kbd class="px-1 py-0.5 rounded border border-stroke-strong dark:border-stroke-strong-dark bg-surface-muted dark:bg-surface-muted-dark font-mono text-[10px]">?</kbd> anywhere to see them. Applies to this device only.
                </span>
              </label>
              <ToggleSwitch v-model="shortcutsEnabled" id="pref-shortcuts-enabled" class="mt-0.5" />
            </div>
          </div>

          <!-- Calendar feed -->
          <div class="flex items-start gap-3 py-4">
            <CalendarDaysIcon class="h-4 w-4 mt-0.5 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            <div class="flex-1 min-w-0">
              <label class="block text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark mb-2">Sync to Google Calendar</label>
              <p v-if="feedError" class="text-xs text-red-600 dark:text-red-400">{{ feedError }}</p>
              <div v-else class="flex items-center gap-2">
                <input
                  :value="feedUrl"
                  readonly
                  @click="$event.target.select()"
                  class="flex-1 min-w-0 px-2 py-1.5 text-xs font-mono rounded border border-stroke-strong dark:border-stroke-strong-dark bg-surface-muted dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark focus:outline-none"
                />
                <button
                  type="button"
                  @click="copyFeedUrl"
                  class="px-3 py-1.5 text-xs font-medium rounded border border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark flex-shrink-0"
                >{{ copied ? 'Copied!' : 'Copy' }}</button>
              </div>
              <button
                type="button"
                @click="regenerateFeedUrl"
                :disabled="regenerating"
                class="mt-1.5 text-xs text-fg-faint dark:text-fg-faint-dark hover:text-fg-secondary dark:hover:text-fg-secondary-dark underline disabled:opacity-50"
              >{{ regenerating ? 'Regenerating…' : 'Regenerate link' }}</button>
            </div>
          </div>
        </template>

        <p v-if="saveError" class="text-sm text-red-600 dark:text-red-400 py-4">{{ saveError }}</p>
      </div>

      <div class="flex justify-end gap-3 px-6 py-4 border-t border-stroke-subtle dark:border-stroke-dark flex-shrink-0">
        <button
          type="button"
          @click="$emit('close')"
          class="px-4 py-2 text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark rounded-lg transition-colors"
        >Cancel</button>
        <button
          v-if="!loading && !loadError"
          type="button"
          @click="submit"
          :disabled="saving"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
        >{{ saving ? 'Saving…' : 'Save' }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { XMarkIcon, BellIcon, KeyIcon, ChevronRightIcon, ChatBubbleLeftRightIcon, EyeSlashIcon, CalendarDaysIcon, CommandLineIcon } from '@heroicons/vue/24/outline'
import { useApi } from '../../composables/useApi.js'
import { useShortcutsEnabled } from '../../composables/useShortcutsEnabled.js'
import ToggleSwitch from './ToggleSwitch.vue'
import NotificationPrefsPanel from './NotificationPrefsPanel.vue'

const props = defineProps({
  focus: { type: String, default: null },  // 'notifications' scrolls that section into view
})
const notificationsSection = ref(null)
const notificationsOpen = ref(props.focus === 'notifications')

const emit = defineEmits(['close', 'saved', 'change-password'])
const { fetchApi } = useApi()

const loading = ref(true)
const loadError = ref('')
const saving = ref(false)
const saveError = ref('')

const hideExcusedLessons = ref(false)
const preferredContactMethod = ref('either')
const { shortcutsEnabled } = useShortcutsEnabled()

const feedUrl = ref('')
const feedError = ref('')
const copied = ref(false)
const regenerating = ref(false)

onMounted(async () => {
  if (props.focus === 'notifications') {
    // Wait for the rest of the modal to render so the scroll lands correctly.
    setTimeout(() => notificationsSection.value?.scrollIntoView({ block: 'start' }), 300)
  }
  try {
    const data = await fetchApi('/api/teacher-availability/me')
    hideExcusedLessons.value = !!data.hide_excused_lessons
    preferredContactMethod.value = data.preferred_contact_method || 'either'
  } catch (e) {
    loadError.value = e.detail || 'Could not load your preferences.'
  } finally {
    loading.value = false
  }

  try {
    const feed = await fetchApi('/api/calendar-feed/me')
    feedUrl.value = feed.feed_url
  } catch (e) {
    feedError.value = e.detail || 'Could not load your calendar feed link.'
  }
})

const copyFeedUrl = async () => {
  try {
    await navigator.clipboard.writeText(feedUrl.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 1500)
  } catch (e) {
    feedError.value = 'Could not copy — select and copy the link manually.'
  }
}

const regenerateFeedUrl = async () => {
  if (!confirm('This will break your current calendar link — Google Calendar will stop syncing until you re-add the new one. Continue?')) return
  regenerating.value = true
  feedError.value = ''
  try {
    const feed = await fetchApi('/api/calendar-feed/regenerate', { method: 'POST' })
    feedUrl.value = feed.feed_url
  } catch (e) {
    feedError.value = e.detail || 'Could not regenerate the link.'
  } finally {
    regenerating.value = false
  }
}

const submit = async () => {
  saveError.value = ''
  saving.value = true
  try {
    await fetchApi('/api/teacher-availability/me', {
      method: 'PUT',
      body: JSON.stringify({
        hide_excused_lessons: hideExcusedLessons.value,
        preferred_contact_method: preferredContactMethod.value,
      }),
    })
    emit('saved', hideExcusedLessons.value)
    emit('close')
  } catch (e) {
    saveError.value = e.detail || 'Something went wrong.'
  } finally {
    saving.value = false
  }
}
</script>
